import { createContext, useContext, useRef, useEffect } from 'react';
import { useCartApi } from './useCartApi';
import { useWishlistApi } from './useWishlistApi';

const PendingActionContext = createContext();

// LocalStorage keys
const PENDING_ACTION_KEY = 'pendingActionData';

export const PendingActionProvider = ({ children }) => {
  const pendingNavigationRef = useRef(null);
  const { addToCartApi } = useCartApi();
  const { addToWishlistApi } = useWishlistApi();

  // Load pending navigation path from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(PENDING_ACTION_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        pendingNavigationRef.current = data.navigationPath;
      }
    } catch {
      localStorage.removeItem(PENDING_ACTION_KEY);
    }
  }, []);

  const storePendingAction = (actionType, productData, navigationPath = null) => {
    const actionData = {
      actionType, // 'addToCart' or 'addToWishlist'
      productData, // { productId, variantId, price, quantity }
      navigationPath,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(actionData));
      pendingNavigationRef.current = navigationPath;
    } catch (error) {
      console.error('Error storing pending action:', error);
    }
  };

  const executePendingAction = async (navigate) => {
    try {
      const stored = localStorage.getItem(PENDING_ACTION_KEY);

      if (!stored && !pendingNavigationRef.current) {
        return false;
      }

      if (stored) {
        const data = JSON.parse(stored);

        // Clear from localStorage immediately
        localStorage.removeItem(PENDING_ACTION_KEY);

        const { actionType, productData, navigationPath } = data;

        if (actionType === 'navigateTo') {
          // Pure navigation — no cart/wishlist action needed, no customerId required
          pendingNavigationRef.current = null;
          if (navigationPath && navigate) {
            navigate(navigationPath);
          }
          return true;
        }

        // Read user ID fresh from localStorage — avoids stale React state
        const storedUser = localStorage.getItem('authUser');
        const userData = storedUser ? JSON.parse(storedUser) : null;
        const customerId = userData?.id || userData?._id || userData?.customerId;

        if (!customerId) {
          return false;
        }

        if (actionType === 'addToCart') {
          await addToCartApi(customerId, productData.variantId, productData.quantity || 1);
        } else if (actionType === 'addToWishlist') {
          await addToWishlistApi(
            customerId,
            productData.productId,
            productData.variantId,
            productData.price
          );
        } else if (actionType === 'navigateTo') {
          // Pure navigation — no cart/wishlist action needed
          pendingNavigationRef.current = null;
          if (navigationPath && navigate) {
            navigate(navigationPath);
          }
          return true;
        }

        pendingNavigationRef.current = null;

        if (navigationPath && navigate) {
          navigate(navigationPath);
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error('Error executing pending action:', error);
      localStorage.removeItem(PENDING_ACTION_KEY);
      return false;
    }
  };

  const clearPendingAction = () => {
    pendingNavigationRef.current = null;
    localStorage.removeItem(PENDING_ACTION_KEY);
  };

  const hasPendingAction = () => {
    return !!localStorage.getItem(PENDING_ACTION_KEY);
  };

  return (
    <PendingActionContext.Provider value={{
      storePendingAction,
      executePendingAction,
      clearPendingAction,
      hasPendingAction
    }}>
      {children}
    </PendingActionContext.Provider>
  );
};

export const usePendingAction = () => {
  const context = useContext(PendingActionContext);
  if (!context) {
    throw new Error('usePendingAction must be used within PendingActionProvider');
  }
  return context;
};
